import React, { useState, KeyboardEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Badge from './Badge';
import clsx from 'clsx';
import FieldShell, { fieldSurface } from './FieldShell';

interface TagInputProps {
  id: string;
  label: string;
  placeholder?: string;
  tags: string[];
  onChange: (tags: string[]) => void;
  description?: string;
  disabled?: boolean;
  error?: string;
}

const TagInput: React.FC<TagInputProps> = ({ id, label, placeholder, tags, onChange, description, disabled, error }) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && inputValue.trim() !== '') {
      e.preventDefault();
      const tagValue = inputValue.trim().replace(/,\s*$/, '');
      if (tagValue && !tags.includes(tagValue)) {
        onChange([...tags, tagValue]);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && inputValue === '' && tags.length > 0) {
      e.preventDefault();
      onChange(tags.slice(0, -1));
    }
  };

  const handleRemoveTag = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  return (
    <FieldShell id={id} label={label} description={description} error={error}>
      <div
        className={clsx(
          fieldSurface({ error: !!error, disabled, multiline: true }),
          'flex items-center flex-wrap gap-1 px-3 py-1.5 h-auto',
          // The real input is a borderless child, so the focus ring has to come
          // from the wrapper via focus-within rather than from `focus-visible`.
          // Mirrors FOCUS_RING at focus-within weight - the real input is a
          // borderless child, so focus-visible would never reach this wrapper.
          !disabled && !error && 'focus-within:bg-surface focus-within:border-champagne/60 focus-within:ring-2 focus-within:ring-champagne/20',
          !disabled && error && 'focus-within:ring-2 focus-within:ring-danger-500/40',
          disabled && 'opacity-60'
        )}
        onClick={() => !disabled && inputRef.current?.focus()}
      >
        {tags.map((tag) => (
          <Badge
            key={tag}
            color="blue"
            onClose={disabled ? undefined : () => handleRemoveTag(tag)}
          >
            {tag}
          </Badge>
        ))}
        <input
          id={id}
          ref={inputRef}
          type="text"
          className="flex-1 min-w-[120px] bg-transparent border-none focus:ring-0 focus:outline-none text-fg-strong text-sm placeholder:text-fg-muted/60 p-0"
          placeholder={tags.length === 0 ? (placeholder || t('Press Enter to add tags')) : ''}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
        />
      </div>
    </FieldShell>
  );
};

export default TagInput;
